import StyledContainer from '@/components/styled/StyledContainer';
import StyledListHeader from '@/components/styled/StyledListHeader';
import StyledNextPreviousButtons from '@/components/styled/StyledNextPreviousButtons';
import StyledSelect from '@/components/styled/StyledSelect';
import StyledTable from '@/components/styled/StyledTable';
import {
  useAcceptCircuitToDistribute,
  useGetDistributorCircuitCountries,
  useGetDistributorCircuitDurations,
  useGetDistributorCircuitReceivers,
  useGetDistributorCircuits,
  useWithdrawCircuit,
} from '@/hooks/useCircuits';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import useTableControls from '@/hooks/useTableControls';
import { distributedCircuitColumns } from '@/types/circuits/circuitColumnDef';
import { LabelValue } from '@/types/global';
import { RowSelectionState } from '@tanstack/react-table';
import { distributorCircuitsListParsers } from '@/utils/searchParams/parsers';
import { useQueryStates } from 'nuqs';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { COUNTRIES } from '../../../../shared/CountriesData';
import StyledInput from '@/components/styled/StyledInput';
import SearchIcon from '@/assets/icons/search.svg?react';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import UndistributedCircuitsList from '@/components/distributorCircuits/UndistributedCircuitsList';
import StyledButton from '@/components/styled/StyledButton';
import ModalDeletion from '@/components/ui/ModalDeletion';
import { useToast } from '@/hooks/useToast';
import { PAGE_SIZE } from '@/constants/pageSize';
import { useNavigate } from 'react-router';
import { PATHS } from '@/constants/Paths';
import PlusIcon from '@/assets/icons/plus.svg?react';
import AcceptCircuitsModal from '@/components/distributorCircuits/AcceptCircuitsModal';
import { useTranslation } from 'react-i18next';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import EditableCircuitsList from '@/components/distributorCircuits/EditableCircuitsList';
import CreateEditableCircuitModal from '@/components/distributorCircuits/CreateEditableCircuitModal';

type DistributorCircuitsFormValues = {
  search: string;
  supplier?: LabelValue<string>;
  countryName?: LabelValue<string>;
  duration?: LabelValue<
    { durationDays: number; durationNights: number } | undefined
  >;
};

type DistributorCircuitsUrlState = {
  name: string;
  supplierid: string | null;
  country: string | null;
  durationDays: string | null;
  durationNights: string | null;
};

function formValuesToUrl(
  values: DistributorCircuitsFormValues,
): DistributorCircuitsUrlState {
  return {
    name: values.search,
    supplierid: values.supplier?.value ?? null,
    country: values.countryName?.value ?? null,
    durationDays: values.duration?.value?.durationDays.toString() ?? null,
    durationNights: values.duration?.value?.durationNights?.toString() ?? null,
  };
}

type tabOptions =
  | 'myCatalogue'
  | 'toBeValidated'
  | 'refused'
  | 'myEditableCircuits';

const CircuitsPage = () => {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const navigate = useNavigate();
  const [urlFilters, setUrlFilters] = useQueryStates(
    distributorCircuitsListParsers,
  );
  const currentTab = urlFilters.circuits;

  const { data: receiverOptions } = useGetDistributorCircuitReceivers({
    isDistributed: currentTab === 'myCatalogue',
    select: (data) => data.map(({ id, name }) => ({ value: id, label: name })),
  });

  const { data: countryOptions } = useGetDistributorCircuitCountries({
    isDistributed: currentTab === 'myCatalogue',
    select: (data) =>
      data?.map(({ country }) => ({ value: country, label: country })),
  });

  const { data: durationOptions } = useGetDistributorCircuitDurations({
    isDistributed: currentTab === 'myCatalogue',
    select: (data) =>
      data?.map(({ durationDays, durationNights }) => ({
        value: { durationDays, durationNights },
        label: durationNights
          ? `${durationDays}j / ${durationNights}n`
          : `${durationDays}j`,
      })),
  });

  const formValues: DistributorCircuitsFormValues = {
    search: urlFilters.name,
    supplier: urlFilters.supplierid
      ? receiverOptions?.find(({ value }) => value === urlFilters.supplierid)
      : undefined,
    countryName: urlFilters.country
      ? { value: urlFilters.country, label: urlFilters.country }
      : undefined,
    duration: urlFilters.durationDays
      ? durationOptions?.find(
          ({ value }) =>
            value.durationDays === Number(urlFilters.durationDays) &&
            (urlFilters.durationNights
              ? value.durationNights === Number(urlFilters.durationNights)
              : !value.durationNights),
        )
      : undefined,
  };

  const { control, subscribe } = useForm<DistributorCircuitsFormValues>({
    values: formValues,
  });

  useEffect(() => {
    const unsub = subscribe({
      formState: { values: true },
      callback: ({ values }) =>
        setUrlFilters(formValuesToUrl(values as DistributorCircuitsFormValues)),
    });
    return () => unsub();
  }, [subscribe, setUrlFilters]);

  const debouncedName = useDebouncedSearch(urlFilters.name);

  const filters = {
    name: debouncedName,
    countries: urlFilters.country ?? '',
    durationDays: urlFilters.durationDays ?? '',
    durationNights: urlFilters.durationNights ?? '',
  };

  const { page, setPage, sorting, setSorting, sortField, sortOrder } =
    useTableControls();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { data: circuitsResponse, isSuccess } = useGetDistributorCircuits({
    page,
    pageSize: PAGE_SIZE,
    sortField,
    sortOrder,
    filters,
  });

  const selectedCircuits = Object.keys(rowSelection);

  const [circuitToDeleteIds, setCircuitToDeleteIds] = useState<string[]>([]);
  const [circuitsToDistributeIds, setCircuitsToDistributeIds] = useState<
    string[]
  >([]);

  const { mutateAsync: distributeCircuit } = useAcceptCircuitToDistribute();
  const { mutateAsync: withdrawCircuit } = useWithdrawCircuit();
  const [
    circuitIdToCreateEditableCircuit,
    setCircuitIdToCreateEditableCircuit,
  ] = useState<string | null>(null);

  const { toastAsync } = useToast();

  return (
    <StyledContainer>
      <StyledListHeader
        title={t('distributorCircuits.list.title')}
        subTitle={t('distributorCircuits.list.subtitle')}
      >
        {selectedCircuits.length > 0 &&
          (currentTab === 'myCatalogue' ? (
            <StyledButton
              title={t('distributorCircuits.list.deleteSelection', {
                count: selectedCircuits.length,
              })}
              className="font-semibold gap-1 px-3 py-2"
              onClick={() => setCircuitToDeleteIds(selectedCircuits)}
              icon={<TrashIcon className="size-5 stroke-brand-600" />}
              variant="outlined"
            />
          ) : (
            <StyledButton
              title={t('distributorCircuits.list.acceptSelection', {
                count: selectedCircuits.length,
              })}
              className="font-semibold gap-1 px-3 py-2"
              onClick={() => setCircuitsToDistributeIds(selectedCircuits)}
              icon={<PlusIcon className="size-5 fill-white" />}
            />
          ))}
      </StyledListHeader>
      <div className="flex flex-col w-full h-auto p-6 gap-6">
        <Tabs
          value={currentTab}
          className="w-full"
          onValueChange={(value) => {
            setUrlFilters((prev) => ({
              ...prev,
              circuits: value as tabOptions,
            }));
            setRowSelection({});
          }}
        >
          <TabsList>
            <TabsTrigger value="myCatalogue" className="hover:cursor-pointer">
              {t('distributorCircuits.list.myCatalogue')}
            </TabsTrigger>
            {isSuccess && (
              <>
                {circuitsResponse.countInvalidatedCircuits > 0 && (
                  <TabsTrigger
                    value="toBeValidated"
                    className="hover:cursor-pointer flex items-center justify-center gap-2"
                  >
                    <span>{t('distributorCircuits.list.toBeValidated')}</span>
                    <div className="size-5 flex justify-center items-center text-warning-100 bg-warning-500 border border-warning-600 rounded-2xl px-1.5 py-0.5">
                      {circuitsResponse.countInvalidatedCircuits}
                    </div>
                  </TabsTrigger>
                )}
                {circuitsResponse.countRefusedCircuits > 0 && (
                  <TabsTrigger
                    value="refused"
                    className="hover:cursor-pointer flex items-center justify-center gap-2"
                  >
                    <span>{t('distributorCircuits.list.refused')}</span>
                    <div className="size-5 flex justify-center items-center text-brand-100 bg-brand-500 border border-brand-600 rounded-2xl px-1.5 py-0.5">
                      {circuitsResponse.countRefusedCircuits}
                    </div>
                  </TabsTrigger>
                )}
                {circuitsResponse.countEditableCircuits > 0 && (
                  <TabsTrigger
                    value="myEditableCircuits"
                    className="hover:cursor-pointer flex items-center justify-center gap-2"
                  >
                    <span>
                      {t('distributorCircuits.list.myEditableCircuits')}
                    </span>
                    <div className="size-5 flex justify-center items-center text-brand-100 bg-brand-500 border border-brand-600 rounded-2xl px-1.5 py-0.5">
                      {circuitsResponse.countEditableCircuits}
                    </div>
                  </TabsTrigger>
                )}
              </>
            )}
          </TabsList>
          <div className="flex flex-col gap-6 w-full h-auto pt-4">
            <div className="flex flex-col gap-2">
              <span className="text-xs font-normal text-gray-600">
                {t('distributorCircuits.list.filterBy')}
              </span>
              <div className="flex flex-col md:flex-row justify-between gap-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <StyledSelect
                    className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
                    control={control}
                    options={receiverOptions ?? []}
                    name="supplier"
                    placeholder={t('distributorCircuits.list.supplier')}
                    isClearable
                  />
                  <StyledSelect
                    className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
                    control={control}
                    options={countryOptions ?? COUNTRIES}
                    name="countryName"
                    placeholder={t('distributorCircuits.list.country')}
                    isClearable
                  />
                  <StyledSelect
                    className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
                    control={control}
                    options={durationOptions ?? []}
                    name="duration"
                    placeholder={t('distributorCircuits.list.duration')}
                    isClearable
                  />
                </div>
                <div className="w-full md:w-1/3 lg:w-1/4 2xl:w-1/5">
                  <StyledInput
                    control={control}
                    name="search"
                    placeholder={t('distributorCircuits.list.quickSearch')}
                    icon={<SearchIcon className="size-5 stroke-gray-500" />}
                  />
                </div>
              </div>
            </div>
            <TabsContent value="myCatalogue" className="flex flex-col gap-4">
              <StyledTable
                columns={getTranslatedConst(distributedCircuitColumns)}
                data={circuitsResponse?.circuits ?? []}
                isLoading={!isSuccess}
                getRowId={(row) => row.circuit.id}
                sorting={sorting}
                setSorting={setSorting}
                setSelection={setRowSelection}
                onRowClick={(row) => {
                  navigate(
                    PATHS.distributors.circuits.details.replace(
                      ':circuitId',
                      row.original.circuit.id,
                    ),
                    {
                      state: {
                        listSearchParams: urlFilters,
                      },
                    },
                  );
                }}
                onViewRow={(row) => {
                  navigate(
                    PATHS.distributors.circuits.details.replace(
                      ':circuitId',
                      row.original.circuit.id,
                    ),
                    {
                      state: {
                        listSearchParams: urlFilters,
                      },
                    },
                  );
                }}
                onDeleteRow={(row) => {
                  setCircuitToDeleteIds([row.original.circuit.id]);
                }}
                onDuplicateRow={(row) => {
                  setCircuitIdToCreateEditableCircuit(row.original.circuit.id);
                }}
              />
              {isSuccess && (
                <StyledNextPreviousButtons
                  page={page}
                  pageSize={PAGE_SIZE}
                  setPage={setPage}
                  dataLength={circuitsResponse.total}
                  label={t('distributorCircuits.list.circuit')}
                />
              )}
            </TabsContent>
            <TabsContent value="toBeValidated">
              <UndistributedCircuitsList
                filters={filters}
                setRowSelection={setRowSelection}
                listSearchParams={urlFilters}
              />
            </TabsContent>
            <TabsContent value="refused">
              <UndistributedCircuitsList
                filters={filters}
                setRowSelection={setRowSelection}
                onlyRefused
                listSearchParams={urlFilters}
              />
            </TabsContent>
            <TabsContent value="myEditableCircuits">
              <EditableCircuitsList
                filters={filters}
                listSearchParams={urlFilters}
              />
            </TabsContent>
          </div>
        </Tabs>
      </div>
      {circuitToDeleteIds.length > 0 && (
        <ModalDeletion
          closeModal={() => {
            setCircuitToDeleteIds([]);
          }}
          actionOnClick={() => {
            if (circuitToDeleteIds.length === 1)
              toastAsync(withdrawCircuit(circuitToDeleteIds[0]), {
                success: t('distributorCircuits.list.circuitRemovedSuccess'),
              });
            else if (selectedCircuits.length > 0) {
              toastAsync(
                Promise.all(selectedCircuits.map((id) => withdrawCircuit(id))),
                {
                  success: t('distributorCircuits.list.circuitsRemovedSuccess'),
                },
              );
            }
            setRowSelection({});
          }}
          type="circuit"
          isMultiple={circuitToDeleteIds.length > 1}
        />
      )}
      {circuitsToDistributeIds.length > 0 && (
        <AcceptCircuitsModal
          closeModal={() => {
            setCircuitsToDistributeIds([]);
          }}
          actionOnClick={async () => {
            await toastAsync(distributeCircuit(circuitsToDistributeIds), {
              success: t('distributorCircuits.list.circuitsDistributedSuccess'),
            });
            setRowSelection({});
          }}
        />
      )}
      {circuitIdToCreateEditableCircuit && (
        <CreateEditableCircuitModal
          closeModal={() => setCircuitIdToCreateEditableCircuit(null)}
          circuitId={circuitIdToCreateEditableCircuit}
        />
      )}
    </StyledContainer>
  );
};

export default CircuitsPage;
